import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';

import { ReactComponent as HandshakeIcon } from '../assets/icons/message/handshake_icon.svg';
import { ReactComponent as DiamondIcon }   from '../assets/icons/message/diamond_icon.svg';

import { STAGE_GRADIENT } from '../constants/rankColors';
import { SpecialOffer } from '../types';

interface InvitationCelebrationProps {
  invitations: SpecialOffer[];
  onLater: () => void;
  onConfirm: () => void;
}

const InvitationCelebration: React.FC<InvitationCelebrationProps> = ({
  invitations,
  onLater,
  onConfirm,
}) => {
  if (invitations.length === 0) return null;
  const primary = invitations[0];

  return (
    <Backdrop
      open
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        // 付録C.5: 上側 青紫 → 下側 マゼンタ紫（左下→右上の対角）
        background: 'linear-gradient(45deg, #7e318e 0%, #534dff 100%)',
        flexDirection: 'column',
        p: 3,
      }}
    >
      {/* 装飾小円 + 封筒アイコン（SVG r=50 相当の ~110px 白縁リング） */}
      <Box
        sx={{
          width: 110,
          height: 110,
          borderRadius: '50%',
          border: '1.5px solid rgba(255,255,255,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
          mt: 1,
        }}
      >
        <HandshakeIcon width={64} height={42} />
      </Box>

      {/* INVITATION */}
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 800,
          fontSize: '34px',
          letterSpacing: 4,
          lineHeight: 1.1,
          textAlign: 'center',
          mb: 1,
        }}
      >
        INVITATION
      </Typography>

      {/* サブタイトル */}
      <Typography
        sx={{
          color: 'rgba(255,255,255,0.9)',
          fontWeight: 400,
          fontSize: '20px',
          textAlign: 'center',
          mb: 3,
        }}
      >
        店長からの招待状
      </Typography>

      {/* 招待状カード（白半透明 opacity 0.2） */}
      <Box sx={{ width: '100%', maxWidth: 330, mb: 4 }}>
        {/* ヘッダー行 */}
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            px: 2,
            py: 1.25,
            mb: '2px',
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: 2,
              textAlign: 'center',
            }}
          >
            Official Career Talk
          </Typography>
        </Box>

        {/* ボディ（タイトル＋メッセージ＋内側白枠） */}
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
            px: 2,
            pt: 2,
            pb: 1.5,
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: '16px',
              mb: 1,
              textAlign: 'center',
            }}
          >
            {primary.title}
          </Typography>
          {primary.message_content && (
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '14px',
                textAlign: 'center',
                lineHeight: 1.7,
                mb: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {primary.message_content}
            </Typography>
          )}

          {/* 内側白枠 / GIFT TICKET（ダイヤモンドアイコン + 2行）詳細がある場合のみ表示 */}
          {primary.reward_content && (
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <DiamondIcon width={34} height={30} style={{ flexShrink: 0 }} />
              <Box>
                <Typography
                  sx={{
                    color: '#f39800',
                    fontWeight: 800,
                    fontSize: '11px',
                    letterSpacing: 1.5,
                    lineHeight: 1.4,
                  }}
                >
                  GIFT TICKET
                </Typography>
                <Typography
                  sx={{
                    color: '#f39800',
                    fontWeight: 700,
                    fontSize: '12px',
                    lineHeight: 1.4,
                  }}
                >
                  {primary.reward_content}
                </Typography>
              </Box>
            </Box>
          )}

          {invitations.length > 1 && (
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '11px',
                textAlign: 'center',
                mt: 1,
              }}
            >
              他 {invitations.length - 1} 件の招待状があります
            </Typography>
          )}
        </Box>
      </Box>

      {/* ボタン行（戻る: グレー90px固定 / 次へ進む: オレンジ残余幅） */}
      <Box sx={{ width: '100%', maxWidth: 330, display: 'flex', gap: '10px' }}>
        <Button
          onClick={onLater}
          variant="contained"
          disableElevation
          sx={{
            flex: '0 0 90px',
            borderRadius: 2.5,
            py: 1.25,
            fontWeight: 700,
            fontSize: '13px',
            bgcolor: '#bdbdbd',
            color: '#3c3c3c',
            '&:hover': { bgcolor: '#aaa' },
          }}
        >
          戻る
        </Button>

        <Button
          onClick={onConfirm}
          variant="contained"
          disableElevation
          sx={{
            flex: 1,
            borderRadius: 2.5,
            py: 1.25,
            fontWeight: 700,
            fontSize: '13px',
            background: 'linear-gradient(90deg, #f39800 0%, #e83828 100%)',
            color: '#fff',
            '&:hover': {
              background: 'linear-gradient(90deg, #e08600 0%, #c0261a 100%)',
            },
          }}
        >
          次へ進む
        </Button>
      </Box>
    </Backdrop>
  );
};

export default InvitationCelebration;
